import React, { useMemo } from 'react';
import { produce } from 'immer';
import TabItem from './TabItem';
import { useExamSessionStore } from '../../../stores/StoreContext';

/**
 * IdentificationTab Component
 * 
 * A specialized tab component for the Identification tab that manages
 * its own subscription to the identity validation state. This prevents the parent
 * SessionDetails component from re-rendering when the identity validation changes.
 * 
 * Shows a red notification dot when identity is not validated (isIdentityValidated = false)
 * Shows a green notification dot when identity is validated (isIdentityValidated = true)
 * 
 * Uses Immer for immutable state updates to ensure optimal performance.
 * 
 * @param {Object} props
 * @param {string} props.tabKey - Unique identifier for the tab (should be 'identification')
 * @param {Object} props.option - Base tab configuration object
 * @param {string} props.option.label - Display label for the tab
 * @param {boolean} props.isSelected - Whether this tab is currently selected
 * @param {Function} props.onClick - Click handler for the tab
 */
function IdentificationTab({ tabKey, option, isSelected, onClick }) {
    // Subscribe to identity validation only in this component
    // This prevents parent re-renders when the identity validation changes
    const isIdentityValidated = useExamSessionStore((state) => state.isIdentityValidated);

    // Create an enhanced option with notification dot based on validation status using Immer
    const enhancedOption = useMemo(
        () =>
            produce(option, (draft) => {
                /*
                For launch, no dots will be shown....
                if (!draft.notification) {
                    draft.notification = {
                        persistWhenSelected: true,
                        showValue: false
                    };
                }
                // Always set persistWhenSelected for identification
                draft.notification.persistWhenSelected = true;
                draft.notification.showValue = false;
                draft.notification.count = 1;
                // Red dot if not validated, green dot if validated
                draft.notification.color = isIdentityValidated ? 'success' : 'danger';
                */
            }),
        [option, isIdentityValidated]
    );

    return (
        <TabItem
            tabKey={tabKey}
            option={enhancedOption}
            isSelected={isSelected}
            onClick={onClick}
        />
    );
}

export default IdentificationTab;
